import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import {
	ErrorResponse,
	SuccessPageResponse,
	SuccessResponse,
	useHttpClient,
} from "api/api"
import {
	Enrollment,
	EnrollmentCountsServer,
	EnrollmentPayload,
	EnrollmentTemplateSuccess,
} from "interface/enrollment"
import {
	TemplatedFormParamsAnswer,
	TemplatedFormQuestion,
} from "interface/templatedForm"
import { useMemo } from "react"
import { useParams } from "react-router"

const LIMIT = 20

export const queryKeys = {
	list: "enrollments",
	single: "enrollment",
	counts: "enrollments-counts",
	enrollPatient: "enroll-patient",
	cancelEnrollment: "cancel-enrollment",
	completeEnrollment: "complete-enrollment",
	adminList: "admin-enrollments",
	adminUpdate: "admin-enrollments-update",
	adminToggle: "admin-enrollments-toggle",
	adminReady: "admin-enrollments-ready",
}

export const useEnrollmentsCounts = (enabled?: boolean) => {
	const { httpClient } = useHttpClient()
	const { scopeId } = useParams()

	return useQuery({
		queryKey: [queryKeys.counts, scopeId],
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<EnrollmentCountsServer>>(
				"/api/v1/enrollments/counts",
				{
					signal,
				},
			)
		},
		enabled: !!scopeId && (enabled !== undefined ? !!enabled : true),
		placeholderData: keepPreviousData,
		select({ data }) {
			const counts = data.data
			return {
				Pending: counts.pending ?? 0,
				"Form completed": counts.formCompleted ?? 0,
				"Ready for fulfillment": counts.readyForFulfillment ?? 0,
				"Installation date set": counts.installationDateSet ?? 0,
				Done: counts.done ?? 0,
				Canceled: counts.canceled ?? 0,
			}
		},
	})
}

export const useEnrollments = ({
	order = 1,
	status,
	fullName,
}: {
	order: number
	status: Array<Enrollment["status"]>
	fullName?: string | null
}) => {
	const { httpClient } = useHttpClient()
	const { scopeId } = useParams()
	const params = {
		order,
		sort: status.includes("Installation date set")
			? "installationDate"
			: "createdAt",
	}

	return useInfiniteQuery({
		queryKey: [queryKeys.list, { ...params, scopeId, status, fullName }],
		queryFn: async ({ pageParam: page = 1, signal }) => {
			const {
				data: { data },
			} = await httpClient.post<SuccessPageResponse<Enrollment>>(
				"/api/v1/enrollments/patients/search",
				{ status, fullName },
				{
					signal,
					params: { page, limit: LIMIT, ...params },
				},
			)

			return {
				page,
				pages: data.pages,
				data: data.result,
			}
		},
		enabled: !!scopeId,
		placeholderData: keepPreviousData,
		initialPageParam: 1,
		select: ({ pageParams, pages }) => {
			return {
				pageParams,
				pages: pages.flatMap(({ data }) => data),
			}
		},
		getNextPageParam: ({ page, pages }) => {
			return page < pages ? page + 1 : null
		},
	})
}

export const useEnrollPatient = () => {
	const { scopeId } = useParams()
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<Enrollment, ErrorResponse, EnrollmentPayload>({
		mutationKey: [queryKeys.enrollPatient],
		mutationFn: async (enrollment) =>
			httpClient.post(
				"/api/v1/enrollments/patient",
				{ ...enrollment, careOrganization: scopeId },
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.counts],
			})
			queryClient.invalidateQueries({
				queryKey: [queryKeys.list],
			})
		},
	})
}

export const useCancelEnrollment = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<Enrollment, ErrorResponse, Enrollment["id"]>({
		mutationKey: [queryKeys.cancelEnrollment],
		mutationFn: (id: Enrollment["id"]) =>
			httpClient.patch(`/api/v1/enrollments/${id}/cancel`),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.counts],
			})
			queryClient.invalidateQueries({
				queryKey: [queryKeys.list],
			})
		},
	})
}

export const useCompleteEnrollment = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: [queryKeys.completeEnrollment],
		mutationFn: (id: Enrollment["id"]) =>
			httpClient.patch(`/api/v1/enrollments/${id}/complete`),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.single],
			})
		},
	})
}

export const useEnrollment = (id: Enrollment["id"]) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.single, id],
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<Enrollment>>(
				`/api/v1/enrollments/${id}`,
				{
					signal,
				},
			)
		},
		enabled: !!id,
		select({ data }) {
			return data.data
		},
	})
}

export const useMedicalNecessityFormTemplate = () => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: ["medical-necessity-form-template"],
		queryFn({ signal }) {
			return httpClient.get<
				SuccessResponse<{
					id: string
					title: string
					description: string
					updatedAt: string
					questions: TemplatedFormQuestion[]
				}>
			>("/api/v1/enrollments/medical-necessity-form-template", {
				signal,
			})
		},
		select({ data }) {
			return data.data.questions
		},
	})
}

export const useSaveMedicalNecessityFormAnswers = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: ["save-medical-necessity-form-answers"],
		mutationFn: ({
			id,
			answers,
		}: {
			id: Enrollment["id"]
			answers: TemplatedFormParamsAnswer[]
		}) =>
			httpClient.patch(`/api/v1/enrollments/${id}/medical-necessity-form`, {
				answers,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.counts],
			})
			queryClient.invalidateQueries({
				queryKey: [queryKeys.list],
			})
		},
	})
}

export const useTemplateURL = (
	template: "consent-form-template" | "patient-brochure",
	enabled = true,
) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: ["template", template],
		queryFn({ signal }) {
			return httpClient.get<EnrollmentTemplateSuccess>(
				`/api/v1/enrollments/${template}`,
				{
					signal,
				},
			)
		},
		enabled,
		select({ data }) {
			return data.data.fileUrl
		},
	})
}

export const useAdminEnrollments = ({
	status,
	sort,
	query,
	page = "1",
	organizations,
	createdAtStart,
	createdAtEnd,
}: {
	status?: Array<Enrollment["status"]>
	sort: string[]
	query?: string
	page?: string
	organizations?: string[]
	createdAtStart?: string
	createdAtEnd?: string
}) => {
	const { httpClient } = useHttpClient()
	const params = useMemo(
		() => ({
			limit: LIMIT,
			page,
			status,
			sort,
			query: query?.trim() || undefined,
			organizations,
			createdAtStart,
			createdAtEnd,
		}),
		[createdAtEnd, createdAtStart, organizations, page, query, sort, status],
	)

	return useQuery({
		queryKey: [queryKeys.adminList, params],
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessPageResponse<Enrollment>>(
				"/api/v1/admin/enrollments/list",
				{
					signal,
					params,
				},
			)

			return data
		},
		placeholderData: keepPreviousData,
	})
}

export const useAdminEnrollmentUpdate = (enrollmentId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<Enrollment, ErrorResponse, EnrollmentPayload>({
		mutationKey: [queryKeys.adminUpdate],
		mutationFn: async (enrollment) => {
			const {
				data: { data },
			} = await httpClient.patch<SuccessResponse<Enrollment>>(
				`/api/v1/admin/enrollments/${enrollmentId}`,
				enrollment,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			)

			return data
		},
		onSuccess: (updatedEnrollment) => {
			queryClient.setQueriesData<SuccessPageResponse<Enrollment>["data"]>(
				{ queryKey: [queryKeys.adminList] },
				(prev) => {
					if (!prev) {
						return prev
					}

					return {
						...prev,
						result: prev.result.map((enrollment) => {
							if (enrollment.id !== enrollmentId) {
								return enrollment
							}

							return {
								...enrollment,
								...updatedEnrollment,
							}
						}),
					}
				},
			)
		},
	})
}

export const useAdminEnrollmentToggle = (operation: "cancel" | "uncancel") => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<Enrollment, ErrorResponse, Enrollment["id"]>({
		mutationKey: [queryKeys.adminToggle],
		mutationFn: async (id: Enrollment["id"]) => {
			const {
				data: { data },
			} = await httpClient.patch(`/api/v1/admin/enrollments/${id}/${operation}`)
			return data
		},
		onSuccess: (updatedEnrollment, enrollmentId) => {
			queryClient.setQueriesData<SuccessPageResponse<Enrollment>["data"]>(
				{ queryKey: [queryKeys.adminList] },
				(prev) => {
					if (!prev) {
						return prev
					}

					return {
						...prev,
						result: prev.result.map((enrollment) => {
							if (enrollment.id !== enrollmentId) {
								return enrollment
							}

							return {
								...enrollment,
								...updatedEnrollment,
							}
						}),
					}
				},
			)
		},
	})
}

export const useAdminEnrollmentReady = (enrollmentId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()
	type EnrollmentReadyPayload = {
		fieldSupportTeamId: string
	}

	return useMutation<Enrollment, ErrorResponse, EnrollmentReadyPayload>({
		mutationKey: [queryKeys.adminReady],
		mutationFn: async (payload) => {
			const {
				data: { data },
			} = await httpClient.post<SuccessResponse<Enrollment>>(
				`/api/v1/admin/enrollments/${enrollmentId}/mark-fulfillment`,
				payload,
			)

			return data
		},
		onSuccess: (updatedEnrollment) => {
			queryClient.setQueriesData<SuccessPageResponse<Enrollment>["data"]>(
				{ queryKey: [queryKeys.adminList] },
				(prev) => {
					if (!prev) {
						return prev
					}

					return {
						...prev,
						result: prev.result.map((enrollment) => {
							if (enrollment.id !== enrollmentId) {
								return enrollment
							}

							return {
								...enrollment,
								...updatedEnrollment,
							}
						}),
					}
				},
			)
		},
	})
}
